import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { masterRunbooksApi } from './api'
import { handleApiError } from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'
import type { MasterRunbook } from './api'
import type { ListParams } from '@/lib/api/types'

// Query keys
export const masterRunbookKeys = {
    all: ['master-runbooks'] as const,
    lists: () => [...masterRunbookKeys.all, 'list'] as const,
    list: (params: ListParams) => [...masterRunbookKeys.lists(), params] as const,
    details: () => [...masterRunbookKeys.all, 'detail'] as const,
    detail: (id: number) => [...masterRunbookKeys.details(), id] as const,
}

// Master Runbooks hooks
export function useMasterRunbooks(params?: ListParams) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: masterRunbookKeys.list(params || {}),
        queryFn: () => masterRunbooksApi.list(params),
        enabled: isAuthenticated && !mfaRequired,
        placeholderData: keepPreviousData,
    })
}

export function useMasterRunbook(id: number) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: masterRunbookKeys.detail(id),
        queryFn: () => masterRunbooksApi.get(id),
        select: (response) => response.data,
        enabled: !!id && isAuthenticated && !mfaRequired,
    })
}

export function useCreateMasterRunbook() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: Omit<MasterRunbook, 'id'>) =>
            masterRunbooksApi.create(data),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: masterRunbookKeys.lists() })
            // Use description from the submitted data (it's always present in the form)
            const description = variables.description || 'Master runbook'
            toast({
                title: 'Master Runbook Created',
                description: `${description} has been created successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useUpdateMasterRunbook() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<Omit<MasterRunbook, 'id'>> }) =>
            masterRunbooksApi.update(id, data),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: masterRunbookKeys.lists() })
            queryClient.invalidateQueries({ queryKey: masterRunbookKeys.detail(variables.id) })
            // Use description from the submitted data (it's always present in the form)
            const description = variables.data.description || 'Master runbook'
            toast({
                title: 'Master Runbook Updated',
                description: `${description} has been updated successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useDeleteMasterRunbook() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => masterRunbooksApi.delete(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: masterRunbookKeys.lists() })
            queryClient.removeQueries({ queryKey: masterRunbookKeys.detail(id) })
            toast({
                title: 'Master Runbook Deleted',
                description: 'Master runbook has been deleted successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}
